import { t } from "../../i18n";
import type {
  CodexSubscriptionUsage,
  OpenCodeUsage,
  Provider,
} from "@lumen/shared";

function usageBar(percent: number) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100 dark:bg-white/10">
      <div
        className={`h-full rounded-full ${clamped >= 90 ? "bg-red-500" : clamped >= 70 ? "bg-amber-500" : "bg-lumen-500"}`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function formatDuration(seconds: number) {
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1)
    return days >= 7 ? `${Math.round(days / 7)}w` : `${days}d ${hours % 24}h`;
  return `${hours}h ${minutes % 60}m`;
}

function usageWindowRow({
  label,
  window,
}: {
  label: string;
  window?: {
    status?: string | null;
    resetInSec?: number | null;
    usagePercent?: number | null;
  } | null;
}) {
  if (!window || window.usagePercent == null) return null;
  const limited =
    window.status === "rate-limited" || window.usagePercent >= 100;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{label}</span>
        <span className="text-stone-500">
          {window.usagePercent}% ·{" "}
          {window.resetInSec != null
            ? t("Resets in {{duration}}", {
                duration: formatDuration(window.resetInSec),
              })
            : t("No reset")}
        </span>
      </div>
      {usageBar(window.usagePercent)}
      {limited && (
        <p className="text-[11px] text-red-600 dark:text-red-300">
          {t("Rate limited")}
        </p>
      )}
    </div>
  );
}

function codexUsageDetails(usage: CodexSubscriptionUsage) {
  if (usage.unavailable)
    return <p className="mt-3 text-xs text-stone-500">{usage.unavailable}</p>;
  if (!usage.connected)
    return (
      <p className="mt-3 text-xs text-stone-500">
        {t("Not signed in to ChatGPT. Sign in to see subscription usage.")}
      </p>
    );
  const windows = usage.rateLimits ?? [];
  const usageWindows = windows.filter(
    (window) =>
      window.usedPercent !== undefined ||
      window.primary?.usedPercent !== undefined,
  );
  const today = new Date().toISOString().slice(0, 10);
  const daily = usage.tokenUsage?.dailyUsageBuckets ?? [];
  const todayBucket = daily.find((bucket) => bucket.startDate === today);
  const latestBucket = [...daily]
    .filter((bucket) => bucket.startDate)
    .sort((left, right) =>
      (right.startDate ?? "").localeCompare(left.startDate ?? ""),
    )[0];
  const tokenBucket = todayBucket ?? latestBucket;
  return (
    <div className="mt-3 space-y-3 text-xs">
      {(usage.planType || usage.email) && (
        <div className="flex flex-wrap gap-2">
          {usage.planType && (
            <span className="rounded-full bg-lumen-50 px-2 py-1 text-lumen-700 dark:bg-lumen-900/30 dark:text-lumen-300">
              {usage.planType}
            </span>
          )}
          {usage.email && (
            <span className="rounded-full bg-stone-100 px-2 py-1 dark:bg-white/5">
              {usage.email}
            </span>
          )}
        </div>
      )}
      {usage.credits?.hasCredits && (
        <p className="flex items-center justify-between">
          <span className="font-medium">{t("Credits")}</span>
          <span>
            {usage.credits.unlimited
              ? t("Unlimited")
              : usage.credits.balance != null
                ? `$${usage.credits.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : usage.credits.usedPercent != null
                  ? `${usage.credits.usedPercent}%`
                  : ""}
          </span>
        </p>
      )}
      {usageWindows.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] font-bold tracking-wider text-stone-500 uppercase">
            {t("Rate limits")}
          </div>
          {usageWindows.map((window, index) => {
            const percent =
              window.usedPercent ?? window.primary?.usedPercent ?? 0;
            const reset =
              window.resetsAt != null
                ? formatDuration(window.resetsAt - Date.now() / 1000)
                : window.windowDurationMins != null
                  ? formatDuration(window.windowDurationMins * 60)
                  : undefined;
            return (
              <div
                key={window.limitId ?? `${window.limitName}-${index}`}
                className="space-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {window.limitName ??
                      window.limitId ??
                      t("Rate limit {{index}}", { index: index + 1 })}
                  </span>
                  <span className="text-stone-500">
                    {percent}%
                    {reset
                      ? ` · ${t("Resets in {{duration}}", { duration: reset })}`
                      : ""}
                  </span>
                </div>
                {usageBar(percent)}
              </div>
            );
          })}
        </div>
      )}
      {usage.tokenUsage && (
        <div className="space-y-1">
          <div className="text-[11px] font-bold tracking-wider text-stone-500 uppercase">
            {t("Token usage")}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-stone-600 dark:text-stone-300">
            {tokenBucket && (
              <span>
                {todayBucket ? t("Today") : tokenBucket.startDate}:{" "}
                {tokenBucket.tokens?.toLocaleString() ?? "—"}
              </span>
            )}
            {usage.tokenUsage.lifetimeTokens != null && (
              <span>
                {t("Lifetime")}:{" "}
                {usage.tokenUsage.lifetimeTokens.toLocaleString()}
              </span>
            )}
            {usage.tokenUsage.peakDailyTokens != null && (
              <span>
                {t("Peak day")}:{" "}
                {usage.tokenUsage.peakDailyTokens.toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}
      {(usage.rateLimitResetCredits?.availableCount ?? 0) > 0 && (
        <p className="text-stone-500">
          {t("reset credits available", {
            count: usage.rateLimitResetCredits?.availableCount,
          })}
        </p>
      )}
    </div>
  );
}

function opencodeUsageDetails(usage: OpenCodeUsage) {
  if (usage.unavailable)
    return <p className="mt-3 text-xs text-stone-500">{usage.unavailable}</p>;
  return (
    <div className="mt-3 space-y-3 text-xs">
      {usageWindowRow({
        label: t("Rolling (5 h)"),
        window: usage.rollingUsage,
      })}
      {usageWindowRow({ label: t("Weekly"), window: usage.weeklyUsage })}
      {usageWindowRow({ label: t("Monthly"), window: usage.monthlyUsage })}
      {usage.useBalance && (
        <p className="text-stone-500">
          {t("Uses Zen balance after subscription limits are reached.")}
        </p>
      )}
      {!usage.rollingUsage && !usage.weeklyUsage && !usage.monthlyUsage && (
        <p className="text-stone-500">{t("No usage data available.")}</p>
      )}
    </div>
  );
}

export function ProviderUsageDetails({
  provider,
  usage,
}: {
  provider: Provider;
  usage: CodexSubscriptionUsage | OpenCodeUsage;
}) {
  if (provider.type === "custom")
    return (
      <p className="mt-3 text-xs text-stone-500">
        {provider.customProviderDisplayName ?? t("Custom provider")}
      </p>
    );
  if (provider.type === "codex-subscription")
    return codexUsageDetails(usage as CodexSubscriptionUsage);
  return opencodeUsageDetails(usage as OpenCodeUsage);
}
